import { useEffect, useState } from 'react'
import AddAddress from '../AddAddress/AddAddress'
import { billingData, Billing } from '../../interface/interfaces'
import './address.scss'
import { useNotification } from '../../context/NotificationContext'
import { addBillingInfo, deleteBilling, getBilling, updateBillingInfo } from '../../api/requests'
import SkeletonLoader from '../SkeletonLoaders/DefaultLoader'
import EmptyDisplay from '../EmptyDisplay/EmptyDisplay'
import { usePrompt } from '../../context/PromptContext'
import { allCountries } from 'country-telephone-data'
import 'animate.css'

const splitPhone = (full: string): { countrycode: string; phone: string } => {
    if (!full) return { countrycode: '', phone: '' }
    const digits = full.replace(/\D/g, '')
    const dialCodes = allCountries.map((c) => c.dialCode).sort((a, b) => b.length - a.length)
    const match = dialCodes.find((dc) => digits.startsWith(dc))
    if (match) {
        return { countrycode: `+${match}`, phone: digits.slice(match.length).slice(-10) }
    }
    if (digits.length > 10) {
        return { countrycode: `+${digits.slice(0, digits.length - 10)}`, phone: digits.slice(-10) }
    }
    return { countrycode: '', phone: digits }
}

const Address = () => {
    const { notify } = useNotification()
    const { initPrompt } = usePrompt()
    const [toggleEdit, setToggleEdit] = useState(false)

    const [billing, setBilling] = useState<Billing>({
        id: '',
        firstname: '',
        lastname: '',
        countrycode:'',
        phone: '',
        address: '',
        country: '',
        state: '',
        city: '',
        zipcode: ''
    })


    const [isSaving, setIsSaving] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const [existingBilling, setExistingBilling] = useState<billingData[]>([])



    const loadExisting = async () => {
        try {
            let data = await getBilling()
            setExistingBilling(data)
        } catch (error: any) {
            notify.error(error.message)
        }
    }


    useEffect(() => {
        const func = async () => {
            setIsLoading(true)
            await loadExisting()
            setIsLoading(false)
        }

        func()
        // eslint-disable-next-line
    }, [])





    const isAllInputValid = (data: Billing) => {

        const requiredFields = ['firstname', 'lastname', 'phone', 'address', 'country', 'state', 'city', 'zipcode'];

        return requiredFields.every((field) => {
            const value = (data as any)[field];
            return typeof value === 'string' && value.trim() !== '';
        });
    };


    function isValidPhoneNumber(phone: string) {
        return /^\+?[0-9\s-]{10,15}$/.test(phone);
    }


    const discardFunc = () => {
        setBilling({
            id: '',
            firstname: '',
            lastname: '',
            phone: '',
            address: '',
            country: '',
            state: '',
            city: '',
            zipcode: '',
            countrycode:''
        })

        setToggleEdit(false)
    }


    const hasAnyCountryCode = (phone: string): boolean => {
  return /^\+\d{1,4}/.test(phone.trim());
};


    const saveFunc = async () => {
        try {

            setIsSaving(true)

            if (!isAllInputValid(billing)) {
                throw Error('Please fill in all required fields')
            }

            if (!/^\d{10}$/.test(billing.phone)) {
                throw Error('Phone number must be exactly 10 digits')
            }

            let fullphone=(`${billing.countrycode}${billing.phone}`).trim()

            if (!isValidPhoneNumber(fullphone)) throw Error('Please enter valid phone number')

            if(!hasAnyCountryCode(fullphone)) throw Error('Please add country code prefix to phone number')

            
            let billingInfo = {
                name: `${billing.firstname} ${billing.lastname}`,
                billing_address: billing.address,
                billing_city: billing.city,
                billing_state: billing.state,
                billing_country: billing.country,
                billing_zip: billing.zipcode,
                billing_phone: fullphone,
                longitude: "",
                latitude: "",
            };


            if(billing.id === ''){
                await addBillingInfo(billingInfo)
            }else{
                await updateBillingInfo(billingInfo,billing.id)
            }

            await loadExisting()

            if(billing.id === ''){
                notify.success('Added billing info succesfully!')
            }else{
                notify.success('Updated billing info!')
            }

            
            discardFunc()
          

        } catch (error: any) {
            notify.error(error.message)
        } finally {
            setIsSaving(false)
        }
    }

    const handleEdit = async (id: string) => {

        let obj = existingBilling.find((data) => {
            return data.id === id
        })

        if (obj) {

            const fullName = obj.name;
            const [firstname, lastname] = fullName.split(" ");

            const { countrycode, phone } = splitPhone(obj.billing_phone)

            setBilling({
                id: obj.id,
                firstname: firstname,
                lastname: lastname,
                countrycode,
                phone,
                address: obj.billing_address,
                country: obj.billing_country,
                state: obj.billing_state,
                city: obj.billing_city,
                zipcode: obj.billing_zip
            })

            setToggleEdit(true)

        }

    }

    const handleAdd = async ()=>{

         setBilling({
            id: '',
            firstname: '',
            lastname: '',
            countrycode: '',
            phone: '',
            address: '',
            country: '',
            state: '',
            city: '',
            zipcode: ''
        })

         setToggleEdit(true)

    }

    const handleDelete = async (id: string) => {

        const callbackFunc = async () => {

            try {
                setIsLoading(true)

                await deleteBilling(id)
                await loadExisting()

                notify.success('Deleted info!')

            } catch (error: any) {
                notify.error(error.message)
            } finally {
                setIsLoading(false)
            }

        }

        initPrompt('Delete Info', 'Do you want to delete this info?', callbackFunc)
    }



    return (<div className='address animate__animated animate__fadeIn animate__fast'>

        <div className='btnpack'>
            <button className='btn full' onClick={handleAdd}>Add new Address</button>
        </div>

        {isLoading && (<SkeletonLoader count={4}></SkeletonLoader>)}

        {!isLoading && (
            <div className='animate__animated animate__fadeIn animate__fast'>

                {existingBilling.length >= 1 && (<div className='addressGrid'>


                    {existingBilling.map((data, idx) => (
                        <div className='eachAddress' key={idx}>

                            <div className='data'>
                                <div className='icon profile'></div>
                                <div className='text'>{data.name}</div>
                            </div>


                            <div className='data'>
                                <div className='icon phone'></div>
                                <div className='text'>{data.billing_phone}</div>
                            </div>


                            <div className='data'>
                                <div className='icon addy'></div>
                                <div className='text'>{data.billing_address}</div>
                            </div>




                            <div className='action-pack'>
                                <button className='btn flexbtn editbtn' onClick={() => handleEdit(data.id!)}>
                                    <div className='icon edit'></div>
                                    <div className='text'>Edit</div>
                                </button>
                                <button className='btn flexbtn deletebtn' onClick={() => handleDelete(data.id!)}>
                                    <div className='text'>Delete</div>
                                    <div className='icon trash'></div>
                                </button>

                            </div>



                        </div>
                    ))}


                </div>)}


                {existingBilling.length < 1 && (<EmptyDisplay text={'No information yet'} icon_class={''} ></EmptyDisplay>)}

            </div>
        )}






        <AddAddress data={billing} toggle={toggleEdit} isSaving={isSaving} setData={setBilling} setToggle={setToggleEdit} saveFunc={saveFunc} discardFunc={discardFunc} ></AddAddress>

    </div>)
}


export default Address